import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../store";
import { storedLocale } from "../../Authentication/util";
import { useGetCbrQuery } from "../../../services/ActionReports/ActionReportAPI";
import type { CbrRecord, CbrResponse } from "../../../services/ActionReports/ActionReportAPI.types";
import { useWatchlistFetch } from "../hooks/useWatchlistFetch";

export const defaultCbrActionReportData = (): CbrResponse => ({
  cbrData: [],
  callResults: [],
});

/**
 * Transforms a CbrRecord into a flat report row for ActionReportListView.
 * CBR KPI formatted strings are placed directly on the row so that
 * getKpiValue can read them via kpiValue (skipEnrichment path).
 */
export const mapCbrToReportData = (records: CbrRecord[], confidentialUserLabel: string) => {
  return records.map((record) => ({
    aboName: record.aboAttributes.privacyFlag ? confidentialUserLabel : record.aboAttributes.aboName,
    aboNumber: String(record.aboNumber),
    abo: String(record.aboNumber),
    aff: record.affiliate,
    level: record.level,
    imageRefUrl: record.imageRefUrl,
    isoCountry: record.aboAttributes.isoCountry,
    // CBR-specific KPIs pre-formatted by the backend
    cbrPeriodRemaining: record.periodsRemaining?.formatted ?? "--",
    cbrVcsPercent: record.personalVcsPercent?.formatted ?? "--",
    cbrStartMonth: record.periodStart?.formatted ?? "--",
    cbrEndMonth: record.endPeriod?.formatted ?? "--",
  }));
};

export const FetchCbrActionReportData = (period: string, skipRequest = false) => {
  const { user } = useAppSelector((state) => state.boot);
  const { t } = useTranslation(["leafAboName"]);
  const confidentialUserLabel = t("ConfidentialUser", "Confidential User", { ns: "leafAboName" });

  const {
    data: payloadData,
    isFetching,
    isError,
    refetch,
  } = useGetCbrQuery(
    {
      Aff: user.aff,
      Abo: user.abo,
      Period: period,
      Locale: storedLocale(),
    },
    {
      skip: skipRequest,
    },
  );

  const { isLoading: isLoadingWatchlist } = useWatchlistFetch();

  const [isLoading, setIsLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(false);
  const [cbrData, setCbrData] = useState<CbrResponse>(defaultCbrActionReportData());

  useEffect(() => {
    setIsLoading(isFetching || isLoadingWatchlist);
    setErrorStatus(isError);

    if (!isFetching && !isError && payloadData) {
      setCbrData(payloadData);
    }
  }, [isFetching, period, isError, payloadData, isLoadingWatchlist]);

  const reportData = mapCbrToReportData(cbrData.cbrData, confidentialUserLabel);

  return { isLoading, cbrData, reportData, errorStatus, refetch };
};
